import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FiMapPin, FiUsers, FiPlus, FiArrowRight, FiCheckCircle } from "react-icons/fi";
import { fetchSites, fetchCustomers } from "../utils/api";
import {
  PageHeader,
  PageTitle,
  PageSubtitle,
  StatGrid,
  Stat,
  Card,
  SectionTitle,
  Skeleton,
} from "../components/ui";

const Greeting = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.space(4)};
  margin-top: ${({ theme }) => theme.space(6)};
`;

const QuickAction = styled(Card)`
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(3)};

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: ${({ theme }) => theme.shadow.sm};
  }

  .icon {
    width: 40px;
    height: 40px;
    border-radius: ${({ theme }) => theme.radius.md};
    background: ${({ theme }) => theme.colors.accentSoft};
    color: ${({ theme }) => theme.colors.accent};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    svg { width: 20px; height: 20px; }
  }
  .title { font-size: 16px; font-weight: 600; }
  .desc { font-size: 13px; color: ${({ theme }) => theme.colors.textMuted}; }
  .cta {
    margin-top: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 600;
  }
`;

const SectionWrap = styled.div`
  margin-top: ${({ theme }) => theme.space(8)};
`;

function Dashboard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ sites: null, active: null, customers: null, complete: null });
  const [loading, setLoading] = useState(true);

  const fullName = auth.user?.profile?.name || "Admin";
  const firstName = fullName.split(" ")[0];

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = auth.user?.id_token;
      try {
        const [sites, customers] = await Promise.all([
          fetchSites(token).catch(() => []),
          fetchCustomers(token).catch(() => []),
        ]);
        if (cancelled) return;
        const sitesArr = Array.isArray(sites) ? sites : [];
        const customersArr = Array.isArray(customers) ? customers : [];
        setStats({
          sites: sitesArr.length,
          active: sitesArr.filter((s) => s.status === 1).length,
          customers: customersArr.length,
          complete: customersArr.filter((c) => c.is_profile_complete).length,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [auth.user?.id_token]);

  const renderStat = (label, value, sub, Icon) => (
    <Stat>
      <div className="label"><Icon /> {label}</div>
      <div className="value">{loading ? <Skeleton h={32} w={80} /> : value ?? "—"}</div>
      {sub && <div className="sub">{sub}</div>}
    </Stat>
  );

  return (
    <>
      <PageHeader>
        <Greeting>
          <PageTitle>Good day, {firstName}</PageTitle>
          <PageSubtitle>Here's what's happening across Carparky today.</PageSubtitle>
        </Greeting>
      </PageHeader>

      <StatGrid>
        {renderStat("Total sites", stats.sites, stats.sites != null ? `${stats.active ?? 0} active` : "", FiMapPin)}
        {renderStat("Active sites", stats.active, stats.sites != null && stats.sites > 0 ? `${Math.round((stats.active / stats.sites) * 100)}% of total` : "", FiCheckCircle)}
        {renderStat("Customers", stats.customers, "", FiUsers)}
        {renderStat("Profiles complete", stats.complete, stats.customers != null && stats.customers > 0 ? `${Math.round((stats.complete / stats.customers) * 100)}% complete` : "", FiCheckCircle)}
      </StatGrid>

      <SectionWrap>
        <SectionTitle>Quick actions</SectionTitle>
        <QuickGrid>
          <QuickAction onClick={() => navigate("/sites/add")} role="button" tabIndex={0}>
            <div className="icon"><FiPlus /></div>
            <div>
              <div className="title">Add a new site</div>
              <div className="desc">Onboard a parking site with geolocation, pricing and availability.</div>
            </div>
            <span className="cta">Get started <FiArrowRight /></span>
          </QuickAction>

          <QuickAction onClick={() => navigate("/sites")} role="button" tabIndex={0}>
            <div className="icon"><FiMapPin /></div>
            <div>
              <div className="title">Manage sites</div>
              <div className="desc">Review and update existing sites, bays and pricing across the network.</div>
            </div>
            <span className="cta">View sites <FiArrowRight /></span>
          </QuickAction>

          <QuickAction onClick={() => navigate("/users")} role="button" tabIndex={0}>
            <div className="icon"><FiUsers /></div>
            <div>
              <div className="title">Manage customers</div>
              <div className="desc">Browse customers, fleets and profile completion.</div>
            </div>
            <span className="cta">View customers <FiArrowRight /></span>
          </QuickAction>
        </QuickGrid>
      </SectionWrap>
    </>
  );
}

export default Dashboard;
